import { Capacitor, registerPlugin } from "@capacitor/core";
import { getCachedAssets, refreshAssetCache, type CachedAsset, type CachedAssetList } from "./assetCache";

/**
 * QuickFillBridge — TypeScript wrapper around the native plugin that ships
 * the asset cache to the Android Autofill Service and opens the system
 * "default autofill service" picker.
 *
 * On iOS / web there is no native counterpart; the methods become no-ops and
 * the UI falls back to in-app copy-to-clipboard.
 */

export interface QuickFillItem {
  id: string;
  name: string;
  listId: string;
  listTitle: string;
  /** "List › Parent › Child" path used as the dataset subtitle in autofill. */
  path: string;
}

export interface QuickFillCachePayload {
  updatedAt: string;
  items: QuickFillItem[];
}

interface QuickFillPlugin {
  syncCache(payload: QuickFillCachePayload): Promise<{ ok: boolean; count: number }>;
  openAutofillSettings(): Promise<{ opened: boolean }>;
  isAutofillEnabled(): Promise<{ enabled: boolean }>;
  getDebugInfo(): Promise<QuickFillDebugInfo>;
  setDebugSetting(opts: { key: string; value: boolean }): Promise<{ ok: boolean }>;
  clearDebugLog(): Promise<{ ok: boolean }>;
  getPendingPick(): Promise<{ pending: boolean }>;
  resolvePick(opts: { value: string }): Promise<{ ok: boolean }>;
  cancelPick(): Promise<{ ok: boolean }>;
}

export interface QuickFillDebugField {
  class: string;
  autofillType: number;
  inputType: number;
  focused: boolean;
  hints: string[];
  hint?: string;
  hasText?: boolean;
  matched: boolean;
  reason: string;
}

export interface QuickFillDebugEvent {
  at: number;
  result: string;
  package?: string;
  activity?: string;
  targetCount: number;
  datasetCount: number;
  durationMs: number;
  fields: QuickFillDebugField[];
}

export interface QuickFillDebugInfo {
  enabled: boolean;
  packageName: string;
  sdkInt: number;
  cachedItemCount: number;
  events: QuickFillDebugEvent[];
  settings: Record<string, boolean>;
}

const QuickFill = registerPlugin<QuickFillPlugin>("QuickFill", {
  // Web fallback — keeps imports working in the browser preview.
  web: {
    async syncCache() {
      return { ok: false, count: 0 };
    },
    async openAutofillSettings() {
      return { opened: false };
    },
    async isAutofillEnabled() {
      return { enabled: false };
    },
    async getDebugInfo() {
      return {
        enabled: false,
        packageName: "(web)",
        sdkInt: 0,
        cachedItemCount: 0,
        events: [],
        settings: {},
      };
    },
    async setDebugSetting() {
      return { ok: false };
    },
    async clearDebugLog() {
      return { ok: false };
    },
    async getPendingPick() {
      return { pending: false };
    },
    async resolvePick() {
      return { ok: false };
    },
    async cancelPick() {
      return { ok: false };
    },
  },
});

export function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export function isIosNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

/**
 * Build the flat item list used by autofill from the cached asset tree.
 * The "path" is built by walking parent_id pointers so the autofill picker
 * can show "MyList › Customer › Email" instead of just "Email".
 */
export function buildQuickFillItems(
  lists: CachedAssetList[],
  assets: CachedAsset[]
): QuickFillItem[] {
  const listTitleById = new Map(lists.map((l) => [l.id, l.title]));
  const assetById = new Map(assets.map((a) => [a.id, a]));

  const items: QuickFillItem[] = [];
  for (const asset of assets) {
    // Skip container assets that have children — the value the user wants
    // to fill is almost always a leaf. Containers stay reachable via the
    // in-app Quick Access screen.
    if (asset.subAssetCount > 0) continue;

    const segments: string[] = [];
    let cursor: CachedAsset | undefined = asset;
    let safety = 16;
    while (cursor && safety-- > 0) {
      segments.unshift(cursor.name);
      cursor = cursor.parent_id ? assetById.get(cursor.parent_id) : undefined;
    }
    const listTitle = listTitleById.get(asset.list_id) ?? "";
    items.push({
      id: asset.id,
      name: asset.name,
      listId: asset.list_id,
      listTitle,
      path: [listTitle, ...segments].filter(Boolean).join(" › "),
    });
  }
  return items;
}

/**
 * Refresh the local cache, then push it to the native autofill service.
 * Safe to call on any platform — becomes a no-op outside Android.
 */
export async function syncQuickFillCache(): Promise<{ ok: boolean; count: number }> {
  await refreshAssetCache();
  if (!isAndroidNative()) return { ok: false, count: 0 };

  const cached = getCachedAssets();
  if (!cached) return { ok: false, count: 0 };

  const payload: QuickFillCachePayload = {
    updatedAt: new Date().toISOString(),
    items: buildQuickFillItems(cached.assetLists, cached.assets),
  };

  try {
    return await QuickFill.syncCache(payload);
  } catch (err) {
    console.warn("[quickfill] syncCache failed", err);
    return { ok: false, count: 0 };
  }
}

export async function openAutofillSettings(): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.openAutofillSettings();
    return res.opened;
  } catch (err) {
    console.warn("[quickfill] openAutofillSettings failed", err);
    return false;
  }
}

export async function isAutofillEnabled(): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.isAutofillEnabled();
    return res.enabled;
  } catch {
    return false;
  }
}

export async function getQuickFillDebugInfo(): Promise<QuickFillDebugInfo | null> {
  if (!isAndroidNative()) return null;
  try {
    return await QuickFill.getDebugInfo();
  } catch (err) {
    console.warn("[quickfill] getDebugInfo failed", err);
    return null;
  }
}

export async function setQuickFillSetting(key: string, value: boolean): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.setDebugSetting({ key, value });
    return res.ok;
  } catch {
    return false;
  }
}

export async function clearQuickFillDebugLog(): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.clearDebugLog();
    return res.ok;
  } catch {
    return false;
  }
}

export async function isQuickFillPickPending(): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.getPendingPick();
    return res.pending;
  } catch {
    return false;
  }
}

export async function resolveQuickFillPick(value: string): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.resolvePick({ value });
    return res.ok;
  } catch (err) {
    console.warn("[quickfill] resolvePick failed", err);
    return false;
  }
}

export async function cancelQuickFillPick(): Promise<boolean> {
  if (!isAndroidNative()) return false;
  try {
    const res = await QuickFill.cancelPick();
    return res.ok;
  } catch {
    return false;
  }
}

export const QUICK_FILL_SETTING_KEYS = [
  "type_text",
  "input_text",
  "input_number",
  "input_phone",
  "input_datetime",
  "classname_heuristic",
  "unfocused_classname",
  "html_input",
  "hint_autofill",
  "verbose_log",
] as const;

export type QuickFillSettingKey = (typeof QUICK_FILL_SETTING_KEYS)[number];

export default QuickFill;